/**
 * Viewer entry point — SPA router, auth, auto-open session
 */

import { loadConfig } from "./config.js";
import { initAuth, requireAuth, isNoAuth, getToken } from "./auth.js";
// Lazy-load heavy modules — live player and recorded viewer are only needed
// when opening a session, not on initial page load.
let _liveModule: typeof import("./live/index.js") | null = null;
async function getLive() {
  if (!_liveModule) _liveModule = await import("./live/index.js");
  return _liveModule;
}
let _recordedModule: typeof import("../DOM/components/recorded.js") | null = null;
async function getRecorded() {
  if (!_recordedModule) _recordedModule = await import("../DOM/components/recorded.js");
  return _recordedModule;
}
import { Router } from "./router/router.js";
import { routes } from "./router/routes.js";
import { initSidebar, initTheme } from "../DOM/components/sidebar.js";
import { bus } from "./core/event-bus.js";
import "../DOM/components/share.js";

// ─── SPA Router Setup ───

const pageContent = document.getElementById("page-content")!;
const sidebar = document.getElementById("sidebar")!;

const router = new Router(pageContent, (path) => {
  bus.emit("route:changed", { path, params: {} });
  // Close live player overlay when navigating to a different page
  if (_liveModule) getLive().then(m => m.closeLive());
});

// All routes
router.addRoutes(routes);

// Init sidebar
const cleanupSidebar = initSidebar(sidebar, location.hash.slice(1) || "/");

// ─── Button handlers ───

let pendingSession: string | null = null;
let pendingShareToken: string | null = null;

document.getElementById("backBtn")!.addEventListener("click", () => getLive().then(m => m.closeLive()));
document.getElementById("recBackBtn")!.addEventListener("click", () => getRecorded().then(m => m.closeRecordedPlayer()));
document.getElementById("unmute")!.addEventListener("click", () => getLive().then(m => m.resumeAudio()));
document.getElementById("quality-select")!.addEventListener("change", (e) => {
  getLive().then(m => m.setQuality((e.target as HTMLSelectElement).value));
});

// Push-to-talk mic capture
const pttBtn = document.getElementById("ptt-btn")!;

function pttStart(): void {
  getLive().then(m => {
    const player = m.getPlayer();
    if (!player) return;
    pttBtn.classList.add("active");
    player.startMic().catch((err) => {
      console.warn("[PTT] Mic start failed:", err);
      pttBtn.classList.remove("active");
    });
  });
}

function pttStop(): void {
  getLive().then(m => {
    const player = m.getPlayer();
    if (player) player.stopMic();
  });
  pttBtn.classList.remove("active");
}

pttBtn.addEventListener("mousedown", (e) => { e.preventDefault(); pttStart(); });
pttBtn.addEventListener("touchstart", (e) => { e.preventDefault(); pttStart(); });
pttBtn.addEventListener("mouseup", pttStop);
pttBtn.addEventListener("mouseleave", pttStop);
pttBtn.addEventListener("touchend", pttStop);
pttBtn.addEventListener("touchcancel", pttStop);
pttBtn.addEventListener("contextmenu", (e) => e.preventDefault());

// Auth login handler
window.addEventListener("auth:login", () => {
  getLive().then(m => {
    if (pendingSession) {
      m.watchLive(pendingSession, pendingShareToken ?? undefined);
      pendingSession = null;
      pendingShareToken = null;
    } else {
      const pending = m.getPendingLiveSession();
      if (pending) {
        m.clearPendingLiveSession();
        m.watchLive(pending.sessionId, pending.shareToken);
      }
    }
  });
});

// Init video player overlay events
getRecorded().then(m => m.initRecordedPlayerEvents());

// Derive session ID and share token from URL
function sessionIdFromUrl(): string | null {
  const m = location.pathname.match(/^\/session\/([^/]+)$/);
  if (m) return m[1];
  return new URLSearchParams(location.search).get("session");
}

function shareTokenFromUrl(): string | null {
  return new URLSearchParams(location.search).get("share");
}

// ─── Boot sequence ───

initTheme();

(async () => {
  await loadConfig();
  await initAuth();

  const sessionId = sessionIdFromUrl();
  const shareToken = shareTokenFromUrl();

  console.log("[boot] sessionId:", sessionId, "shareToken:", !!shareToken, "token:", !!getToken(), "noAuth:", isNoAuth());

  // If a specific session is in the URL, open it directly
  if (sessionId) {
    const live = await getLive();
    const authNeeded = live.watchLive(sessionId, shareToken ?? undefined);
    if (authNeeded) {
      pendingSession = sessionId;
      pendingShareToken = shareToken;
    }
    return;
  }

  // Start the router — handles all SPA routes and "/play/*"
  router.start();
})();
